import React, { useContext, useEffect, useState } from "react";
import { Switch } from "@mui/material";
import styles from "./styles/ProposedField.module.css";
import { switchSx } from "./PartnerSharing";
import { LanguageContext } from "../../../Context/changeLanguageProvider";

/*
  One field a partner has asked to change, answered where it sits.

  A partner cannot change anything on this account. What they can do is
  propose, and a proposal waits against the record until its owner says yes
  or no -- which is what this switch is for. Previous keeps what the customer
  has; New takes what the partner suggests. Nothing is decided until Save.

  On the field's own label line, pushed to the far end, one per proposal --
  so three suggested changes are three switches in three places rather than a
  list somewhere else. The values are not repeated here: flipping the switch
  writes into the field below, so the field itself already shows what saving
  would keep, and printing the same two values again only invites the reader
  to compare them with each other instead of with the field.

  Given the label as `children`, it returns that label untouched when there
  is nothing to ask -- so a field nobody has proposed a change to renders
  exactly as it always did, with no extra wrapper to shift it.
*/

const FALLBACK = {
  partnerChangesPrevious: "Previous",
  partnerChangesNew: "New",
};

/*
  The sharing switches' shape, in this review's colour.

  Green reads as "on", and this is not an on/off: it is a choice between the
  customer's value and the partner's. So it takes the purple that marks
  everything a partner has touched.
*/
const reviewSwitchSx = {
  ...switchSx,
  "& .MuiSwitch-switchBase": {
    ...switchSx["& .MuiSwitch-switchBase"],
    "&.Mui-checked": {
      ...switchSx["& .MuiSwitch-switchBase"]["&.Mui-checked"],
      "& + .MuiSwitch-track": {
        backgroundColor: "#CB30E0",
        opacity: 1,
        border: 0,
      },
    },
  },
};

export default function ProposedField({
  // The field this answers for, e.g. "asset_name".
  field,
  /*
    field -> { value }, already decrypted by the caller: this component never
    touches a key. Absent means there is nothing to ask.
  */
  proposals,
  // field -> true when the customer has chosen the partner's value.
  decisions,
  onDecide,
  // For the switch's accessible name, since nothing here is labelled.
  label,
  /*
    The field's own label. Returned alone when there is no question to ask,
    and paired with the switch when there is. Omitted where the caller has
    somewhere of its own to put this.
  */
  children = null,
  // False on the partner's screen: they are the ones asking.
  active = true,
  disabled = false,
}) {
  const websiteCurrentLang = useContext(LanguageContext).language;
  const [content, setContent] = useState(
    websiteCurrentLang?.content?.User?.EditAsset?.PartnerSharing || {}
  );
  useEffect(() => {
    setContent(
      websiteCurrentLang?.content?.User?.EditAsset?.PartnerSharing || {}
    );
  }, [websiteCurrentLang]);
  const t = (key) => content?.[key] || FALLBACK[key];

  // After the hooks: a component may not call fewer of them on some renders.
  if (!active || !proposals?.[field]) return children;

  const taken = decisions?.[field] === true;
  const name = String(label || field || "").replace(/_/g, " ");

  const control = (
    <div className={styles.row}>
      <span className={styles.side}>{t("partnerChangesPrevious")}</span>
      <Switch
        sx={reviewSwitchSx}
        checked={taken}
        disabled={disabled}
        onChange={(e) => onDecide(field, e.target.checked)}
        inputProps={{ "aria-label": `${name} — ${t("partnerChangesNew")}` }}
      />
      <span className={styles.side}>{t("partnerChangesNew")}</span>
    </div>
  );

  if (!children) return control;

  return (
    <div className={styles.label_row}>
      {children}
      {control}
    </div>
  );
}
